import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck,
  Search,
  LogOut,
  Building2,
  Loader2,
  Save,
  RotateCcw,
  CheckCircle2,
  XCircle,
  SlidersHorizontal,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  CreditCard,
  Check,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { PermissionGridEditor } from "../../components/PermissionGridEditor";
import {
  blankPermissions,
  fullAccessPermissions,
  panelCatalogForPlan,
  restrictPermissionsToCatalog,
  enabledCountForCatalog,
} from "../../utils/accessPermissions";
import type { PanelPermission } from "../../api/access";
import type { PanelLevel } from "../../config/panelCatalog";
import {
  listAdmins,
  getAdminPermissions,
  setAdminPermissions,
  clearAdminPermissions,
  setAdminStatus,
  getAdminSubscription,
  setAdminPlan,
  type SuperadminAdmin,
  type AdminSubscription,
  type WorkspacePlan,
} from "../../api/superadmin";

// The three workspace plans, in upgrade order. "starter" is the free plan,
// "professional" unlocks everything, "enterprise" is the custom plan whose
// panels the superadmin picks in the Access grid.
const PLAN_OPTIONS: {
  value: WorkspacePlan;
  label: string;
  blurb: string;
}[] = [
  {
    value: "starter",
    label: "Free",
    blurb: "Limited workspace — core HR panels only, up to 2 employees.",
  },
  {
    value: "professional",
    label: "Professional",
    blurb: "Full access — every panel unlocked, unlimited employees.",
  },
  {
    value: "enterprise",
    label: "Custom",
    blurb: "Unlimited employees; you pick the panels under Access.",
  },
];

// What add_employee actually enforces (routers/employee.py): a limited
// workspace — Free (starter), or an enterprise sign-up still in sales_pending —
// is capped at 2; Professional and granted Custom are unlimited and any stored
// employee_limit on those rows is ignored. Mirrored here so the dialog never
// reports a limit the API doesn't apply.
const employeeLimitLabel = (plan?: string | null, status?: string | null) =>
  plan === "starter" || status === "sales_pending"
    ? "2 employees"
    : "unlimited employees";

export default function SuperadminConsole() {
  const { user, logout } = useAuth();

  const [admins, setAdmins] = useState<SuperadminAdmin[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [search, setSearch] = useState("");

  // Filters + pagination.
  const [planFilter, setPlanFilter] = useState<
    "all" | "starter" | "professional" | "enterprise" | "none"
  >("all");
  const [emailFilter, setEmailFilter] = useState<string>(""); // "" = all
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailQuery, setEmailQuery] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 9;
  const emailBoxRef = useRef<HTMLDivElement>(null);

  // Plan modal state.
  const [planOpen, setPlanOpen] = useState(false);
  const [planTargetId, setPlanTargetId] = useState<string | null>(null);
  const [planDraft, setPlanDraft] = useState<WorkspacePlan>("starter");
  const [planInfo, setPlanInfo] = useState<AdminSubscription | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  // Professional means "sees everything", so upgrading also clears any custom
  // panel matrix by default — unchecking keeps the restrictions in place.
  const [grantFullAccess, setGrantFullAccess] = useState(true);

  // Access modal state.
  const [accessOpen, setAccessOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, PanelPermission>>({});
  const [governed, setGoverned] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => admins.find((a) => a.user_id === selectedId) || null,
    [admins, selectedId]
  );

  const planTarget = useMemo(
    () => admins.find((a) => a.user_id === planTargetId) || null,
    [admins, planTargetId]
  );

  // A free/starter (or sales-pending) workspace is "limited" — only the
  // free-plan panels are actually available to that admin (mirrors the app's
  // workspace_mode = "limited" rule). We restrict the grid to that plan so the
  // superadmin only sees/toggles what the admin can really use.
  const isLimitedAdmin = (a?: SuperadminAdmin | null) =>
    !!a &&
    ((a.plan || "").toLowerCase() === "starter" ||
      (a.subscription_status || "").toLowerCase() === "sales_pending");

  const planCatalog = useMemo(
    () => panelCatalogForPlan(isLimitedAdmin(selected)),
    [selected]
  );

  const loadAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      setAdmins(await listAdmins());
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load admins.");
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [search, planFilter, emailFilter]);

  // Close the email dropdown when clicking outside it.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (emailBoxRef.current && !emailBoxRef.current.contains(e.target as Node)) {
        setEmailOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openAccess = useCallback(async (admin: SuperadminAdmin) => {
    setSelectedId(admin.user_id);
    setAccessOpen(true);
    setLoadingPerms(true);
    try {
      const res = await getAdminPermissions(admin.user_id);
      // Restrict everything to the admin's PLAN catalog (a free/starter
      // workspace only has the free-plan panels).
      const pc = panelCatalogForPlan(isLimitedAdmin(admin));
      // Open the grid reflecting the admin's REAL current access:
      //  - governed → their saved matrix.
      //  - not governed → full access WITHIN their plan (the permissive
      //    fallback lets them use every panel their plan allows).
      const base = res.governed
        ? blankPermissions(res.permissions)
        : fullAccessPermissions();
      setDraft(restrictPermissionsToCatalog(base, pc));
      setGoverned(res.governed);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load permissions.");
      setDraft(
        restrictPermissionsToCatalog(
          fullAccessPermissions(),
          panelCatalogForPlan(isLimitedAdmin(admin))
        )
      );
      setGoverned(false);
    } finally {
      setLoadingPerms(false);
    }
  }, []);

  const setVisible = (key: string, visible: boolean) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], visible } }));
  const setLevel = (key: string, level: PanelLevel) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], level } }));

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      // Never persist panels outside the admin's plan.
      await setAdminPermissions(
        selectedId,
        restrictPermissionsToCatalog(draft, planCatalog)
      );
      setGoverned(true);
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === selectedId ? { ...a, governed: true } : a))
      );
      toast.success("Panel access saved. Applies on the admin's next page load.");
      setAccessOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await clearAdminPermissions(selectedId);
      setGoverned(false);
      setDraft(restrictPermissionsToCatalog(fullAccessPermissions(), planCatalog));
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === selectedId ? { ...a, governed: false } : a))
      );
      toast.success("Stopped governing — this admin now sees everything again.");
      setAccessOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't clear access.");
    } finally {
      setSaving(false);
    }
  };

  const openPlan = useCallback(async (admin: SuperadminAdmin) => {
    setPlanTargetId(admin.user_id);
    setPlanOpen(true);
    setPlanInfo(null);
    // Seed from the list row so the dialog is never empty, then confirm with
    // the live subscription (which also carries status / employee limit).
    setPlanDraft(((admin.plan || "starter").toLowerCase() as WorkspacePlan));
    setGrantFullAccess(true);
    setLoadingPlan(true);
    try {
      const sub = await getAdminSubscription(admin.user_id);
      setPlanInfo(sub);
      if (sub.plan) setPlanDraft(sub.plan);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load the plan.");
    } finally {
      setLoadingPlan(false);
    }
  }, []);

  const handleSavePlan = async () => {
    if (!planTargetId) return;
    setSavingPlan(true);
    try {
      const res = await setAdminPlan(planTargetId, planDraft, {
        grantFullAccess: planDraft === "professional" && grantFullAccess,
      });
      setAdmins((prev) =>
        prev.map((a) =>
          a.user_id === planTargetId
            ? {
                ...a,
                plan: res.plan,
                subscription_status: res.status,
                governed: res.governed,
              }
            : a
        )
      );
      setPlanInfo(res);
      toast.success(
        `Plan set to ${planBadge(res.plan).label}. Applies on the admin's next sign-in.`
      );
      setPlanOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't change the plan.");
    } finally {
      setSavingPlan(false);
    }
  };

  const toggleActive = async (admin: SuperadminAdmin) => {
    const next = !admin.is_active;
    try {
      await setAdminStatus(admin.user_id, next);
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === admin.user_id ? { ...a, is_active: next } : a))
      );
      toast.success(next ? "Admin activated." : "Admin deactivated.");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't change status.");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((a) => {
      if (
        q &&
        !(
          a.name.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          (a.company_name || "").toLowerCase().includes(q)
        )
      )
        return false;
      if (emailFilter && a.email !== emailFilter) return false;
      if (planFilter !== "all") {
        const p = (a.plan || "").toLowerCase();
        if (planFilter === "none" ? !!p : p !== planFilter) return false;
      }
      return true;
    });
  }, [admins, search, planFilter, emailFilter]);

  // Pagination — 9 cards per page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const paged = filtered.slice(
    (pageClamped - 1) * PAGE_SIZE,
    pageClamped * PAGE_SIZE
  );

  // Distinct emails for the email filter dropdown (searchable).
  const emailOptions = useMemo(() => {
    const all = Array.from(new Set(admins.map((a) => a.email))).sort();
    const q = emailQuery.trim().toLowerCase();
    return q ? all.filter((e) => e.toLowerCase().includes(q)) : all;
  }, [admins, emailQuery]);

  const grantedCount = enabledCountForCatalog(draft, planCatalog);

  // Workspace plan → { label, badge classes }. starter=Free, professional,
  // enterprise=Custom. Unknown/none reads as "No plan".
  const planBadge = (plan?: string | null) => {
    const p = (plan || "").toLowerCase();
    if (p === "starter")
      return { label: "Free", cls: "bg-slate-100 text-slate-600" };
    if (p === "professional")
      return { label: "Professional", cls: "bg-blue-100 text-blue-700" };
    if (p === "enterprise")
      return { label: "Custom", cls: "bg-amber-100 text-amber-700" };
    return { label: "No plan", cls: "bg-gray-100 text-gray-400" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-bold text-gray-900 leading-tight">
                Superadmin Console
              </h1>
              <p className="text-[11px] text-gray-500 truncate">
                {user?.email} · governs {admins.length} admin
                {admins.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <button
            onClick={() => logout()}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="mb-5 flex flex-col md:flex-row md:items-center gap-3">
          {/* Free-text search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search admins by name, email or company…"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>

          {/* Plan filter */}
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value as typeof planFilter)}
            className="py-2.5 px-3 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <option value="all">All plans</option>
            <option value="starter">Free</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Custom</option>
            <option value="none">No plan</option>
          </select>

          {/* Email filter — searchable dropdown */}
          <div ref={emailBoxRef} className="relative">
            <button
              type="button"
              onClick={() => setEmailOpen((o) => !o)}
              className="w-full md:w-64 inline-flex items-center justify-between gap-2 py-2.5 px-3 text-sm rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            >
              <span className="inline-flex items-center gap-2 min-w-0">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate">{emailFilter || "All emails"}</span>
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
            {emailOpen && (
              <div className="absolute z-20 mt-1 left-0 w-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      autoFocus
                      value={emailQuery}
                      onChange={(e) => setEmailQuery(e.target.value)}
                      placeholder="Search email…"
                      className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEmailFilter("");
                      setEmailOpen(false);
                      setEmailQuery("");
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                      !emailFilter ? "text-indigo-600 font-semibold" : "text-gray-600"
                    }`}
                  >
                    All emails
                  </button>
                  {emailOptions.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => {
                        setEmailFilter(e);
                        setEmailOpen(false);
                        setEmailQuery("");
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs truncate hover:bg-gray-50 ${
                        emailFilter === e ? "text-indigo-600 font-semibold" : "text-gray-600"
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                  {emailOptions.length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400">No matching email.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Result count */}
        {!loadingAdmins && filtered.length > 0 && (
          <p className="text-[11px] text-gray-400 mb-3">
            Showing {(pageClamped - 1) * PAGE_SIZE + 1}–
            {Math.min(pageClamped * PAGE_SIZE, filtered.length)} of {filtered.length} admin
            {filtered.length === 1 ? "" : "s"}
          </p>
        )}

        {/* Admin cards */}
        {loadingAdmins ? (
          <div className="py-20 flex justify-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No admins found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {paged.map((a) => (
              <div
                key={a.user_id}
                className={`bg-white rounded-2xl border shadow-sm p-4 flex flex-col gap-3 transition-shadow hover:shadow-md ${
                  a.is_active ? "border-gray-100" : "border-red-100 bg-red-50/20"
                }`}
              >
                {/* Identity */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 flex items-center justify-center shrink-0 text-sm font-bold text-indigo-800">
                    {(a.name || a.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 truncate">
                      {a.name}
                    </h3>
                    <p className="text-[11px] text-gray-500 truncate">{a.email}</p>
                  </div>
                </div>

                {/* Company + badges */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {a.company_name && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full max-w-full truncate">
                      <Building2 className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{a.company_name}</span>
                    </span>
                  )}
                  {(() => {
                    const pb = planBadge(a.plan);
                    return (
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pb.cls}`}
                        title="Workspace plan"
                      >
                        {pb.label}
                      </span>
                    );
                  })()}
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      a.is_active
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {a.is_active ? "Active" : "Deactivated"}
                  </span>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      a.governed
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {a.governed ? "Governed" : "Full access"}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-auto pt-1">
                  <button
                    onClick={() => toggleActive(a)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-2 rounded-lg border transition-colors ${
                      a.is_active
                        ? "border-red-200 text-red-600 hover:bg-red-50"
                        : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                    }`}
                  >
                    {a.is_active ? (
                      <>
                        <XCircle className="w-4 h-4" /> Deactivate
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" /> Activate
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => openPlan(a)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-2 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors"
                    title="Change workspace plan"
                  >
                    <CreditCard className="w-4 h-4" /> Plan
                  </button>
                  <button
                    onClick={() => openAccess(a)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-2 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    <SlidersHorizontal className="w-4 h-4" /> Access
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination (9 per page) */}
        {!loadingAdmins && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped <= 1}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-xs text-gray-500 font-medium">
              Page {pageClamped} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped >= totalPages}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Workspace plan modal */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-lg p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-base font-bold text-gray-900">
              {planTarget ? `${planTarget.name} — Workspace plan` : "Workspace plan"}
            </DialogTitle>
            {planTarget && (
              <p className="text-xs text-gray-500">
                {planTarget.email}
                {planTarget.company_name ? ` · ${planTarget.company_name}` : ""}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-0.5">
              {loadingPlan
                ? "Loading current plan…"
                : planInfo?.plan
                ? `Currently ${planBadge(planInfo.plan).label} · ${
                    planInfo.status ?? "—"
                  } · ${employeeLimitLabel(planInfo.plan, planInfo.status)}`
                : "No subscription yet — picking a plan creates one."}
            </p>
          </DialogHeader>

          <div className="px-5 py-4 space-y-2">
            {PLAN_OPTIONS.map((opt) => {
              const active = planDraft === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPlanDraft(opt.value)}
                  disabled={loadingPlan || savingPlan}
                  className={`w-full text-left rounded-xl border px-4 py-3 transition-colors disabled:opacity-60 ${
                    active
                      ? "border-indigo-500 bg-indigo-50/60 ring-1 ring-indigo-200"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {opt.label}
                    </span>
                    <span className="flex items-center gap-2">
                      {planInfo?.plan === opt.value && (
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                          Current
                        </span>
                      )}
                      {active && <Check className="w-4 h-4 text-indigo-600" />}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">{opt.blurb}</p>
                </button>
              );
            })}

            {/* Professional means "sees everything" — offer to drop any custom
                panel restrictions the admin currently has. */}
            {planDraft === "professional" && (
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={grantFullAccess}
                  onChange={(e) => setGrantFullAccess(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-indigo-600"
                />
                <span className="text-[11px] text-gray-500">
                  Also give full panel access — removes any custom panel
                  restrictions set for this admin.
                </span>
              </label>
            )}

            <p className="text-[11px] text-gray-400 pt-1">
              The new plan reaches the admin's app on their next sign-in.
            </p>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              onClick={() => setPlanOpen(false)}
              className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePlan}
              disabled={savingPlan || loadingPlan}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingPlan ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save plan
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Access panel modal */}
      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-bold text-gray-900">
              {selected ? `${selected.name} — Panel access` : "Panel access"}
            </DialogTitle>
            {selected && (
              <p className="text-xs text-gray-500">
                {selected.email}
                {selected.company_name ? ` · ${selected.company_name}` : ""}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-0.5">
              {governed
                ? `${grantedCount} panel${grantedCount === 1 ? "" : "s"} enabled`
                : "Not governed yet — this admin currently sees everything."}
            </p>
          </DialogHeader>

          <div className="px-5 py-4">
            {loadingPerms ? (
              <div className="py-16 flex justify-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <PermissionGridEditor
                permissions={draft}
                setVisible={setVisible}
                setLevel={setLevel}
                panelCatalog={planCatalog}
              />
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-100 sticky bottom-0 bg-white flex items-center justify-end gap-2">
            {governed && (
              <button
                onClick={handleClear}
                disabled={saving || loadingPerms}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" /> Reset to full access
              </button>
            )}
            <button
              onClick={() => setAccessOpen(false)}
              className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loadingPerms}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save access
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
